import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { IMAGES } from "../../content";
import { ease } from "./Chrome";

const MaskedLine = ({ children, delay, className = "" }) => (
  <div className="overflow-hidden">
    <motion.div
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  </div>
);

export default function Hero({ t, onCta1, onCta2 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} id="cover" className="relative h-screen overflow-hidden" data-testid="section-cover">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Golf & Sea villa" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0c0b] via-[#0a0c0b]/35 to-[#0a0c0b]/50" />

      <div className="relative z-10 h-full flex flex-col justify-end px-6 md:px-12 pb-28 md:pb-32">
        <MaskedLine delay={0.2} className="text-xs md:text-sm tracking-[0.4em] uppercase text-[#d4af37] mb-6">
          {t.cover.kicker}
        </MaskedLine>
        <h1 className="font-serif-display font-light leading-[0.9] tracking-tight text-[#f2f0e9]">
          <MaskedLine delay={0.4} className="text-6xl sm:text-7xl md:text-8xl lg:text-[9rem]">
            <span dir="ltr">{t.cover.title1} <span className="italic text-[#d4af37]">{t.cover.titleAmp}</span></span>
          </MaskedLine>
          <MaskedLine delay={0.6} className="text-6xl sm:text-7xl md:text-8xl lg:text-[9rem]">
            {t.cover.title2}
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.85} className="mt-8 text-base md:text-lg text-[#f2f0e9]/80 max-w-xl font-light">
          {t.cover.subtitle}
        </MaskedLine>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <button onClick={onCta1} className="btn-gold" data-testid="hero-cta-buyers">{t.cover.cta1}</button>
          <button onClick={onCta2} className="btn-ghost" data-testid="hero-cta-investors">{t.cover.cta2}</button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-[#8c877d]"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">{t.cover.scroll}</span>
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ChevronDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
}
